import React, { useState, useEffect, useCallback } from 'react';
import { MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { X } from 'lucide-react';
import ImageUploadPreview from '../ui/ImageUpload/ImageUploadPreview';
import { uploadImage } from '../../api/hotelinfoApi';
import { deleteRemovedImages } from '../../utils/imageUtils';
import {
  Drawer,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  Switch,
  CircularProgress
} from '@mui/material';

export default function RestaurantFormModal({ open, onClose, initialData = {}, onSubmit }) {
  const [form, setForm] = useState({
    name: '',
    subtitle: '',
    about: '',
    address: '',
    mapEmbed: '',
    bookingRequired: false,
    bookingContact: '',
    menu: [],
    openingHours: [],
    photos: [],
    ...initialData
  });
  const [uploadingImages, setUploadingImages] = useState(false);

useEffect(() => {
    const initialForm = {
      name: '',
      subtitle: '',
      about: '',
      address: '',
      mapEmbed: '',
      bookingRequired: false,
      bookingContact: '',
      menu: [],
      openingHours: [],
      photos: [],
      chargeOptionGroups: [],
      tax: { enabled: false, percentage: 0 },
      serviceCharge: { enabled: false, percentage: 0 },
      ...initialData,
    };
    setForm(initialForm);
  }, [initialData]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onSubmit(form);
    setTimeout(() => {
      deleteRemovedImages(initialData?.photos || [], form.photos || []);
    }, 500);
  };

  const handleImageUrlsChange = useCallback((updatedUrls) => {
    setForm(prev => ({ ...prev, photos: updatedUrls }));
  }, []);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 800, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {form._id ? 'Edit Restaurant' : 'Add Restaurant'}
        </Typography>
        <TextField
          label="Name"
          fullWidth
          margin="normal"
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
        />
        <TextField
          label="Subtitle"
          fullWidth
          margin="normal"
          value={form.subtitle}
          onChange={(e) => handleChange('subtitle', e.target.value)}
        />
        <TextField
          label="About"
          fullWidth
          multiline
          rows={3}
          margin="normal"
          value={form.about}
          onChange={(e) => handleChange('about', e.target.value)}
        />
        <TextField
          label="Address"
          fullWidth
          margin="normal"
          value={form.address}
          onChange={(e) => handleChange('address', e.target.value)}
        />
        <TextField
          label="Map Embed URL"
          fullWidth
          margin="normal"
          value={form.mapEmbed}
          onChange={(e) => handleChange('mapEmbed', e.target.value)}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.bookingRequired}
              onChange={(e) => handleChange('bookingRequired', e.target.checked)}
            />
          }
          label="Booking Required"
          sx={{ mt: 2 }}
        />
        {form.bookingRequired && (
          <TextField
            label="Booking Contact"
            fullWidth
            margin="normal"
            value={form.bookingContact}
            onChange={(e) => handleChange('bookingContact', e.target.value)}
          />
        )}
        {/* Charge Option Groups Section */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>Charge Option Groups</Typography>
          {(form.chargeOptionGroups || []).map((group, groupIndex) => (
            <Box key={groupIndex} mb={3} p={2} border="1px solid #ccc" borderRadius={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <TextField
                  label="Group Name"
                  value={group.groupName}
                  onChange={(e) => {
                    const updatedGroups = [...form.chargeOptionGroups];
                    updatedGroups[groupIndex].groupName = e.target.value;
                    handleChange('chargeOptionGroups', updatedGroups);
                  }}
                  variant="standard"
                  sx={{ flexGrow: 1 }}
                />
                <IconButton color="error" onClick={() => {
                  const updatedGroups = form.chargeOptionGroups.filter((_, i) => i !== groupIndex);
                  handleChange('chargeOptionGroups', updatedGroups);
                }}>
                  <X size={18} />
                </IconButton>
              </Box>
              <Box mt={2}>
                {(group.choices || []).map((choice, choiceIndex) => (
                  <Box key={choiceIndex} display="flex" gap={1} alignItems="center" mb={1}>
                    <TextField label="Choice Name" value={choice.name} onChange={(e) => {
                      const updatedGroups = [...form.chargeOptionGroups];
                      updatedGroups[groupIndex].choices[choiceIndex].name = e.target.value;
                      handleChange('chargeOptionGroups', updatedGroups);
                    }} size="small" sx={{ flex: 1 }}/>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Type</InputLabel>
                      <Select value={choice.type} label="Type" onChange={(e) => {
                        const updatedGroups = [...form.chargeOptionGroups];
                        updatedGroups[groupIndex].choices[choiceIndex].type = e.target.value;
                        handleChange('chargeOptionGroups', updatedGroups);
                      }}>
                        <MenuItem value="percentage">Percentage</MenuItem>
                        <MenuItem value="fixed">Fixed</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField label="Value" type="number" value={choice.value} onChange={(e) => {
                      const updatedGroups = [...form.chargeOptionGroups];
                      updatedGroups[groupIndex].choices[choiceIndex].value = parseFloat(e.target.value) || 0;
                      handleChange('chargeOptionGroups', updatedGroups);
                    }} size="small" sx={{ width: 90 }}/>
                    <FormControlLabel control={<Checkbox checked={choice.isDefault} onChange={(e) => {
                      const updatedGroups = [...form.chargeOptionGroups];
                      // Ensure only one default per group
                      updatedGroups[groupIndex].choices.forEach((c, i) => {
                        c.isDefault = i === choiceIndex ? e.target.checked : false;
                      });
                      handleChange('chargeOptionGroups', updatedGroups);
                    }} />} label="Default" />
                    <IconButton color="error" size="small" onClick={() => {
                      const updatedGroups = [...form.chargeOptionGroups];
                      updatedGroups[groupIndex].choices = updatedGroups[groupIndex].choices.filter((_, i) => i !== choiceIndex);
                      handleChange('chargeOptionGroups', updatedGroups);
                    }}>
                      <X size={16} />
                    </IconButton>
                  </Box>
                ))}
                <Button size="small" onClick={() => {
                  const updatedGroups = [...form.chargeOptionGroups];
                  updatedGroups[groupIndex].choices.push({ name: '', type: 'fixed', value: 0, isDefault: false });
                  handleChange('chargeOptionGroups', updatedGroups);
                }}>Add Choice</Button>
              </Box>
            </Box>
          ))}
          <Button variant="outlined" onClick={() => {
            const updated = [...(form.chargeOptionGroups || []), { groupName: '', choices: [{ name: '', type: 'fixed', value: 0, isDefault: true }] }];
            handleChange('chargeOptionGroups', updated);
          }}>
            Add Charge Group
          </Button>
        </Box>

        {/* Taxes & Service Charge Section */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>Taxes & Service Charge</Typography>

          {/* Tax Toggle + Percentage */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FormControlLabel
              control={<Switch checked={!!form?.tax?.enabled} onChange={(e) => {
                const enabled = e.target.checked;
                setForm(prev => ({
                  ...prev,
                  tax: { enabled, percentage: enabled ? (prev?.tax?.percentage ?? 0) : (prev?.tax?.percentage ?? 0) }
                }));
              }} />}
              label="Enable Tax"
            />
            <TextField
              label="Tax Percentage"
              type="number"
              value={form?.tax?.percentage ?? 0}
              onChange={(e) => {
                const pct = parseFloat(e.target.value) || 0;
                setForm(prev => ({ ...prev, tax: { ...(prev?.tax || {}), percentage: pct } }));
              }}
              disabled={!form?.tax?.enabled}
              inputProps={{ step: '0.1', min: '0' }}
              sx={{ width: 200 }}
              helperText="Applies on subtotal."
            />
          </Box>

          {/* Service Charge Toggle + Percentage */}
          <Box display="flex" alignItems="center" gap={2}>
            <FormControlLabel
              control={<Switch checked={!!form?.serviceCharge?.enabled} onChange={(e) => {
                const enabled = e.target.checked;
                setForm(prev => ({
                  ...prev,
                  serviceCharge: { enabled, percentage: enabled ? (prev?.serviceCharge?.percentage ?? 0) : (prev?.serviceCharge?.percentage ?? 0) }
                }));
              }} />}
              label="Enable Service Charge"
            />
            <TextField
              label="Service Charge Percentage"
              type="number"
              value={form?.serviceCharge?.percentage ?? 0}
              onChange={(e) => {
                const pct = parseFloat(e.target.value) || 0;
                setForm(prev => ({ ...prev, serviceCharge: { ...(prev?.serviceCharge || {}), percentage: pct } }));
              }}
              disabled={!form?.serviceCharge?.enabled}
              inputProps={{ step: '0.1', min: '0' }}
              sx={{ width: 260 }}
              helperText="Commonly 5–10%."
            />
          </Box>
        </Box>

        {/* Menu Section */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>Menu</Typography>
          {form.menu.map((item, index) => (
            <Box key={index} display="flex" gap={2} alignItems="center" mb={1}>
              <TextField
                label="Name"
                value={item.name || ''}
                onChange={(e) => {
                  const updated = [...form.menu];
                  updated[index].name = e.target.value;
                  handleChange('menu', updated);
                }}
                size="small"
                sx={{ flex: 2 }}
              />
              <TextField
                label="Description"
                value={item.description || ''}
                onChange={(e) => {
                  const updated = [...form.menu];
                  updated[index].description = e.target.value;
                  handleChange('menu', updated);
                }}
                size="small"
                sx={{ flex: 3 }}
              />
              <TextField
                label="Price"
                type="number"
                value={item.price || ''}
                onChange={(e) => {
                  const updated = [...form.menu];
                  updated[index].price = parseFloat(e.target.value);
                  handleChange('menu', updated);
                }}
                size="small"
                sx={{ width: 100 }}
              />
              <FormControl size="small" sx={{ width: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={item.type || 'food'}
                  onChange={(e) => {
                    const updated = [...form.menu];
                    updated[index].type = e.target.value;
                    handleChange('menu', updated);
                  }}
                >
                  <MenuItem value="food">Food</MenuItem>
                  <MenuItem value="drinks">Drinks</MenuItem>
                  <MenuItem value="coffee">Coffee</MenuItem>
                  <MenuItem value="cocktail">Cocktail</MenuItem>
                </Select>
              </FormControl>
              
              <IconButton
                color="error"
                onClick={() => {
                  const updated = [...form.menu];
                  updated.splice(index, 1);
                  handleChange('menu', updated);
                }}
              >
                <X size={18} />
              </IconButton>
            </Box>
          ))}
          <Button
            variant="outlined"
            onClick={() => {
              const updated = [...form.menu, { name: '', description: '', price: 0, type: 'food', options: [] }];
              handleChange('menu', updated);
            }}
          >
            Add Menu Item
          </Button>
        </Box>

        {/* Opening Hours Section */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>Opening Hours</Typography>
          {form.openingHours.map((entry, index) => (
            <Box key={index} display="flex" gap={2} alignItems="center" mb={1}>
              <TextField
                label="Day"
                value={entry.dayOfWeek || ''}
                onChange={(e) => {
                  const updated = [...form.openingHours];
                  updated[index].dayOfWeek = e.target.value;
                  handleChange('openingHours', updated);
                }}
                size="small"
                sx={{ width: 120 }}
              />
              <TextField
                label="Open"
                type="time"
                value={entry.open || ''}
                onChange={(e) => {
                  const updated = [...form.openingHours];
                  updated[index].open = e.target.value;
                  handleChange('openingHours', updated);
                }}
                size="small"
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Close"
                type="time"
                value={entry.close || ''}
                onChange={(e) => {
                  const updated = [...form.openingHours];
                  updated[index].close = e.target.value;
                  handleChange('openingHours', updated);
                }}
                size="small"
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
              <IconButton
                color="error"
                onClick={() => {
                  const updated = [...form.openingHours];
                  updated.splice(index, 1);
                  handleChange('openingHours', updated);
                }}
              >
                <X size={18} />
              </IconButton>
            </Box>
          ))}
          <Button
            variant="outlined"
            onClick={() => {
              const updated = [...form.openingHours, { dayOfWeek: '', open: '', close: '' }];
              handleChange('openingHours', updated);
            }}
          >
            Add Opening Hour
          </Button>
        </Box>
        {/* Photos Section */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>Photos</Typography>
          <ImageUploadPreview
            initialUrls={form.photos || []}
            onChange={handleImageUrlsChange}
            uploadImageFn={async (file, onProgress) => {
              setUploadingImages(true);
              const url = await uploadImage(file, onProgress);
              setUploadingImages(false);
              return url;
            }}
          />
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ ml: 1 }}
            disabled={uploadingImages}
            startIcon={uploadingImages ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}